import * as moment from 'moment'
const momentDurationFormatSetup = require('moment-duration-format')
momentDurationFormatSetup(moment)
import * as convert from 'conversions'
import {IntervalRaceType, IPace, PaceType, TimeUnit,
  DistanceUnit, IDistance} from '@eventops/athlinks-results-model'
import {DisplayData} from '.'
import {UnitLog} from './unitLog'
import {convertTime, DistanceConversionArgs, TimeConversionArgs} from '..'
import {convertDistance, convertDistanceValue} from './distance'
import {XOR} from '.'
import {convertTimeValue} from './time'
import {isBoolean, partialRight, flow, reduce} from 'lodash'
import map = require('lodash/map')

export interface OverridePaceUnits {
  distanceUnit: DistanceUnit,
  timeUnit: TimeUnit,
  label?: string
}

interface SubPaceLabelSet extends OverridePaceUnits {
  label: string
}

export interface PaceLabelSet extends SubPaceLabelSet {
  paceType: PaceType,
}

export interface PaceArgs {
  conversionArgs?: XOR<TimeConversionArgs,  DistanceConversionArgs>,
  intervalRaceType?: IntervalRaceType,
  capitalizeLabel?: boolean,
  overridePaceUnits?: OverridePaceUnits
}

// default time precision

// hours minutes seconds for default time

export enum PaceLabel {
  MilesPerHour = 'mph',
  KilometersPerHour = 'kph',
  HectaMeterPerMinute = '100m/min',
  HectaYardPerMinute = '100yd/min',
  MinutePerMile = 'min/mi',
  MinutePerKilometer = 'min/km',
  MinutePerHectaYard = 'min/100yd',
  MinutePerHectaMeter = 'min/100m'
}

interface PaceLabelCollection {
  [dividend: string]: {
    [divisor: string]: PaceLabelSet
  }
}

interface PaceLabelMap {
  [pace: string]: PaceLabelCollection
}

interface IPaceLabelBuilder {
  (subSet: SubPaceLabelSet): PaceLabelCollection
}

const paceishLabelSetBuilder = (
  paceType: PaceType
) => (
  subSet: SubPaceLabelSet
) => {
  const {
    distanceUnit,
    label,
    timeUnit
  } = subSet
  return {
    [distanceUnit]: {
      [timeUnit]: {
        distanceUnit,
        label,
        paceType,
        timeUnit
      }
    }
  } as PaceLabelCollection
}

const speedLabelSetBuilder: IPaceLabelBuilder =
  paceishLabelSetBuilder(PaceType.SPEED)
const paceLabelSetBuilder: IPaceLabelBuilder =
  paceishLabelSetBuilder(PaceType.PACE)

const
   hour = TimeUnit.HOURS,
   minute = TimeUnit.MINUTES,
   kilometers = DistanceUnit.KILO_METERS,
   hectaMeters = DistanceUnit.HECTA_METERS,
   miles = DistanceUnit.MILES,
   hectaYards = DistanceUnit.HECTA_YARDS

const speedLabelSet: SubPaceLabelSet[] = [
  {
    distanceUnit: miles,
    timeUnit: hour,
    label: PaceLabel.MilesPerHour
  }, {
    distanceUnit: kilometers,
    timeUnit: hour,
    label: PaceLabel.KilometersPerHour
  }, {
    distanceUnit: hectaMeters,
    timeUnit: minute,
    label: PaceLabel.HectaMeterPerMinute
  }, {
    distanceUnit: hectaYards,
    timeUnit: minute,
    label: PaceLabel.HectaYardPerMinute
  }
]

const paceLabelSet: SubPaceLabelSet[] = [
  {
    distanceUnit: miles,
    timeUnit: minute,
    label: PaceLabel.MinutePerMile
  }, {
    distanceUnit: kilometers,
    timeUnit: minute,
    label: PaceLabel.MinutePerKilometer
  }, {
    distanceUnit: hectaMeters,
    timeUnit: minute,
    label: PaceLabel.MinutePerHectaMeter
  }, {
    distanceUnit: hectaYards,
    timeUnit: minute,
    label: PaceLabel.MinutePerHectaYard
  }
]

export const getPaceLabels = (paceType: PaceType) => {
  switch (paceType) {
    case PaceType.PACE: return paceLabelSet
    case PaceType.SPEED: return speedLabelSet
    default: return undefined
  }
}

const paceMapBuilder = (
  paceLabelBuilder: IPaceLabelBuilder
) => (
  labelSet: SubPaceLabelSet[]
) => flow(
  partialRight(
    map,
    paceLabelBuilder
  ),
  partialRight(
    reduce,
    (
      paceLabelCollection: PaceLabelCollection,
      smallCollection: PaceLabelCollection
    ) => ({
      ...paceLabelCollection,
      ...smallCollection
    }),
    {}
  )
)(labelSet)

const buildPaceLabelMap = (): PaceLabelMap => ({
  [PaceType.SPEED]: paceMapBuilder(speedLabelSetBuilder)(speedLabelSet),
  [PaceType.PACE]: paceMapBuilder(paceLabelSetBuilder)(paceLabelSet)
}) as unknown as PaceLabelMap

const defaultPaceLabelMap: PaceLabelMap = buildPaceLabelMap()

export function findPaceLabel(
  paceType: PaceType,
  distanceUnit: DistanceUnit,
  timeUnit: TimeUnit
): string {
  try {
    return defaultPaceLabelMap[paceType][distanceUnit][timeUnit].label
  } catch {
    return undefined
  }
}

export function findLabel(
  pace: IPace,
  capitalize: boolean
): string {
  const {
    time: { timeUnit },
    distance: { distanceUnit },
    paceType
  } = pace
  if (paceType === PaceType.NOTCALCULATED) {
    return pace.time.timeUnit
  }
  const computedLabel =
    (paceType === PaceType.SPEED) ? `${distanceUnit}/${timeUnit}` :
        `${timeUnit}/${distanceUnit}`
  const paceLabel = findPaceLabel(paceType, distanceUnit, timeUnit)
  const maybeLabel = paceLabel ? paceLabel : computedLabel
  let label: string = undefined
  if (!maybeLabel) {
    UnitLog.warn('You asked for a default label, ' +
      'but none could be found. Use ' +
      `the label '${computedLabel}' or ignore the label type.`)
      label = computedLabel
  } else {
    label = maybeLabel
  }
  return (capitalize) ? label.toUpperCase() : label
}

export interface INormalizedPaceUnit {
  timeUnit: TimeUnit,
  distanceUnit: DistanceUnit,
  paceType: PaceType
}

export interface PaceNormalizer {
  [x: string]: () => INormalizedPaceUnit
}

function buildNormalizedPaceMap(): PaceNormalizer {
  let paceMap = {}

  const mph = {
    timeUnit: TimeUnit.HOURS,
    distanceUnit: DistanceUnit.MILES,
    paceType: PaceType.SPEED
  }

  const minPerMile = {
    timeUnit: TimeUnit.MINUTES,
    distanceUnit: DistanceUnit.MILES,
    paceType: PaceType.PACE
  }

  const hectaMeterPerMin = {
    timeUnit: TimeUnit.MINUTES,
    distanceUnit: DistanceUnit.HECTA_METERS,
    paceType: PaceType.PACE
  }

  paceMap[IntervalRaceType.CYCLING] = () => mph
  paceMap[IntervalRaceType.RUNNING] = () => minPerMile
  paceMap[IntervalRaceType.SWIM] = () => hectaMeterPerMin
  paceMap[IntervalRaceType.TRANSITION] = () => minPerMile
  paceMap[IntervalRaceType.UNDEFINED] = () => minPerMile

  return paceMap
}

const normalizedPaceMap: PaceNormalizer = buildNormalizedPaceMap()

export function normalizePace(
  pace: IPace,
  intervalRaceType: IntervalRaceType
): IPace {
  if (pace.paceType !== PaceType.NOTCALCULATED) {
    return pace
  }
  const {
    distance,
    time
  } = pace
  if (!intervalRaceType) {
    UnitLog.warn('Could not find interval race type. ' +
      'Defaulting to MIN/MI as your pace type.')
  }
  const {
    distanceUnit,
    timeUnit,
    paceType
  } = normalizedPaceMap[intervalRaceType]()
  return {
    distance: {
      distanceInMeters: distance.distanceInMeters,
      distanceUnit
    },
    time: {
      timeInMillis: time.timeInMillis,
      timeUnit
    },
    paceType
  }
}

export function mapUnitsForMoment(timeUnit: TimeUnit):
  'minutes' | 'milliseconds' | 'seconds' | 'hours' {
  switch (timeUnit) {
    case TimeUnit.MILLI_SECONDS:
      return 'milliseconds'
    case TimeUnit.SECONDS:
      return 'seconds'
    case TimeUnit.HOURS:
      return 'hours'
    case TimeUnit.MINUTES:
      return 'minutes'
  }
}

export function mapUnitsForConversions(distanceUnit: DistanceUnit): string {
  switch (distanceUnit) {
    case DistanceUnit.FEET:
      return 'feet'
    case DistanceUnit.HECTA_METERS:
    case DistanceUnit.METERS:
      return 'meters'
    case DistanceUnit.HECTA_YARDS:
    case DistanceUnit.YARDS:
      return 'yards'
    case DistanceUnit.KILO_METERS:
      return 'kilometers'
    case DistanceUnit.MILES:
      return 'miles'
  }
}

export enum UnitType {
  DEFAULT = 'default',
  METRIC = 'metric',
  IMPERIAL = 'imperial'
}

// only support changing mph to kph and min/mi to min/km
const getMilesOrKilosDistanceUnit = (unitType: UnitType) => {

  if (unitType === UnitType.IMPERIAL) {
    return DistanceUnit.MILES
  }

  return DistanceUnit.KILO_METERS
}

export function getPaceFromUnitType(pace: IPace, unitType: UnitType) {

  const defaultPace: IPace = { ...pace }

  if (unitType === UnitType.DEFAULT) {
    return defaultPace
  }

  const distUnit = pace.distance.distanceUnit
  const convertingMilesOrKm = distUnit === DistanceUnit.MILES
    || distUnit === DistanceUnit.KILO_METERS

  if (!convertingMilesOrKm) {
    return defaultPace
  }

  const mappedDistanceUnit = getMilesOrKilosDistanceUnit(unitType)

  const newDistance: IDistance = {
    distanceInMeters: pace.distance.distanceInMeters,
    distanceUnit: mappedDistanceUnit
  }

  const newPace: IPace = {
    ...pace,
    distance: newDistance
  }

  return newPace
}

export function calculatePaceValue(pace: IPace, args: PaceArgs): string {
  const {
    time,
    time: {
      timeUnit
    },
    distance,
    distance: {
      distanceUnit
    },
    paceType
  } = pace
  const {
    intervalRaceType
  } = args
  const timeArgsTemp = args as TimeConversionArgs
  const roundUpSeconds = isBoolean(timeArgsTemp.roundUpSeconds) ?
    timeArgsTemp.roundUpSeconds : false
  const timeArgs = {
    ...timeArgsTemp,
    roundUpSeconds
  }
  const distArgsTemp = args.conversionArgs as DistanceConversionArgs |
    { precision: undefined}
  const precision = (distArgsTemp && distArgsTemp.precision) ?
    distArgsTemp.precision : 2
  const distArgs = {
    ...distArgsTemp,
    precision
  }
  switch (paceType) {
    case PaceType.NOTCALCULATED:
    case PaceType.PACE:
      // transitions are a special case
      if (intervalRaceType === IntervalRaceType.TRANSITION) {
        return convertTime(time, args.conversionArgs as TimeConversionArgs).value
      }
      const timeForDistance =
        convertTimeValue(time) /
        convertDistanceValue(distance, distArgs)

      const timeInMillis = moment.duration(
        timeForDistance, mapUnitsForMoment(timeUnit)).asMilliseconds()
      return convertTime({
        timeInMillis,
        timeUnit
      }, timeArgs).value
    case PaceType.SPEED:
      const distForTime =
        convertDistanceValue(distance, distArgs) /
        convertTimeValue(time)
      const distanceInMeters =
        (distanceUnit === DistanceUnit.HECTA_METERS ||
         distanceUnit === DistanceUnit.HECTA_YARDS) ?
        convert(
          distForTime * 100,
          mapUnitsForConversions(distanceUnit),
          'meters'
        ) / 100 :
        convert(
          distForTime,
          mapUnitsForConversions(distanceUnit),
          'meters'
        )
      return convertDistance(
        {
          distanceInMeters,
          distanceUnit
        }, distArgs
      ).value.toString()
  }
}

export function checkForOverridePaceUnits(pace: IPace,
  args: PaceArgs = {}): IPace {
    const {
      overridePaceUnits
    } = args
    return (overridePaceUnits) ? {
      ...pace,
      distance: {
        distanceInMeters: pace.distance.distanceInMeters,
        distanceUnit: overridePaceUnits.distanceUnit
      },
      time: {
        timeInMillis: pace.time.timeInMillis,
        timeUnit: overridePaceUnits.timeUnit
      }
    } : pace
  }

export function convertPace(
  pace: IPace,
  args: PaceArgs = {}
): DisplayData<string> {
  const {
    capitalizeLabel = true,
    intervalRaceType
  } = args
  const {
    distance: {
      distanceInMeters,
      distanceUnit
    },
    time: {
      timeInMillis,
      timeUnit
    }
  } = checkForOverridePaceUnits(pace)

  if (distanceInMeters < 0 || timeInMillis < 0) {
    UnitLog.warn(`Negative values found in pace (distance = ` +
      `${distanceInMeters}, time = ${timeInMillis}), returning empty pace.`)
    return {
      value: '',
      label: ''
    }
  }

  const normalizedPace = normalizePace(pace, intervalRaceType)
  const {
    paceType
  } = normalizedPace
  try {
    return {
      value: calculatePaceValue(normalizedPace, args),
      label: findLabel(normalizedPace, capitalizeLabel)
   }
  } catch (err) {
    if (paceType === PaceType.SPEED) {
      UnitLog.warn(
        `Could not convert ` +
        `${distanceInMeters} meters per ${timeInMillis} ms ` +
        `to pace '${distanceUnit}/${timeUnit}'.`
      )
      return {
        value: `${distanceInMeters / timeInMillis}`,
        label: `${DistanceUnit.METERS}/${TimeUnit.MILLI_SECONDS}`
      }
    } else {
      UnitLog.warn(
        `Could not convert ` +
        `${timeInMillis} ms per ${distanceInMeters} meters ` +
        `to pace '${timeUnit}/${distanceUnit}'.`
      )
      return {
        value: `${timeInMillis / distanceInMeters}`,
        label: `${TimeUnit.MILLI_SECONDS}/${DistanceUnit.METERS}`
      }
    }
  }
}